import {
  ListItemButton,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Typography,
  useTheme,
  BoxProps,
  CircularProgress,
} from '@mui/material'
import React from 'react'
import { SvgIconComponent } from '@mui/icons-material'

type ProviderOptionBoxProps = {
  title: React.ReactNode | string | null
  iconComponent: SvgIconComponent
  description?: React.ReactNode | string
  onClick?: () => void
  sx?: BoxProps['sx']
  iconBackgroundColor?: string
  'data-testid'?: string
  disabled?: boolean
  loading?: boolean
}

const OptionBox: React.FC<ProviderOptionBoxProps> = ({
  title = null,
  iconComponent,
  description,
  onClick,
  sx,
  iconBackgroundColor,
  'data-testid': dataTestId,
  disabled = false,
  loading = false,
}: ProviderOptionBoxProps): JSX.Element => {
  const theme = useTheme()

  const finalBgColor = iconBackgroundColor ?? theme.palette.secondary.light

  const iconColor = theme.palette.getContrastText(finalBgColor)

  return (
    <ListItemButton
      onClick={onClick}
      alignItems="center"
      disabled={disabled || loading}
      sx={{
        display: 'flex',
        flexDirection: 'row',
        padding: theme.spacing(2),
        width: '100%',
        height: 88,
        cursor: disabled || loading ? 'not-allowed' : 'pointer',
        bgcolor: 'background.paper',
        '&:hover': {
          backgroundColor: disabled || loading ? 'inherit' : theme.palette.grey[50],
        },
        opacity: disabled || loading ? 0.5 : 1,
        ...sx,
      }}
      data-testid={dataTestId}
    >
      <ListItemAvatar sx={{ minWidth: 'auto', marginRight: theme.spacing(2) }}>
        <Avatar
          sx={{
            width: 40,
            height: 40,
            backgroundColor: finalBgColor,
          }}
        >
          {loading ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            React.cloneElement(React.createElement(iconComponent), { sx: { color: iconColor, width: 24, height: 24 } })
          )}
        </Avatar>
      </ListItemAvatar>
      <ListItemText
        sx={{ padding: theme.spacing(0.5, 0), margin: 0 }}
        primary={
          <Typography
            sx={{
              fontFamily: theme.typography.fontFamily,
              fontSize: 14,
              lineHeight: '24px',
              fontWeight: theme.typography.fontWeightRegular,
              color: 'text.primary',
            }}
          >
            {title}
          </Typography>
        }
        secondary={
          description && (
            <Typography
              sx={{
                fontFamily: theme.typography.fontFamily,
                fontSize: 12,
                lineHeight: '24px',
                fontWeight: theme.typography.fontWeightRegular,
                color: 'text.secondary',
              }}
            >
              {description}
            </Typography>
          )
        }
      />
    </ListItemButton>
  )
}

export default OptionBox
